import { StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { DESKTOP_CONSTANTS } from '@/constants/admin';

/**
 * Themed stylesheet for DesktopDataTable + sibling components (header, pagination).
 * Converted from a static export to a hook so dark/light theme toggle re-themes the
 * entire data-table cluster automatically. Layout dimensions stay static; colors flow
 * from `admin.dataTable.*` and `redesign` tokens.
 */
export const useDesktopDataTableStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: 'transparent',
      },
      tableHeader: {
        backgroundColor: admin.dataTable.headerBg,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
        height: DESKTOP_CONSTANTS.TABLE_HEADER_HEIGHT,
      },
      headerScrollView: {
        flex: 1,
      },
      headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: '100%',
        width: '100%',
      },
      headerCell: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
        minHeight: DESKTOP_CONSTANTS.TABLE_HEADER_HEIGHT,
      },
      headerText: {
        fontWeight: '600',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: admin.dataTable.text,
      },
      sortIndicator: {
        marginLeft: 4,
      },
      sortIndicatorText: {
        fontSize: 12,
        lineHeight: 12,
      },
      tableBody: {
        flex: 1,
        minHeight: 0,
      },
      tableRow: {
        height: DESKTOP_CONSTANTS.TABLE_ROW_HEIGHT,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.row,
      },
      tableRowAlt: {
        backgroundColor: admin.dataTable.rowAlt,
      },
      selectedRow: {
        backgroundColor: admin.selector.itemSelectedBg,
      },
      rowScrollView: {
        flex: 1,
      },
      rowContent: {
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: '100%',
        width: '100%',
      },
      cell: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: DESKTOP_CONSTANTS.TABLE_ROW_HEIGHT,
      },
      cellText: {
        fontSize: 14,
        color: admin.dataTable.text,
      },
      arrayCellContainer: {
        width: '100%',
        maxHeight: 72,
      },
      arrayCellScroll: {
        width: '100%',
      },
      checkbox: {
        width: 20,
        height: 20,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
      },
      paginationContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.headerBg,
      },
      paginationText: {
        fontSize: 14,
        color: colors.text.dim,
      },
      paginationLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
      },
      paginationButtons: {
        flexDirection: 'row',
        gap: 8,
      },
      rowsPerPageContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      },
      rowsPerPageLabel: {
        fontSize: 14,
        color: colors.text.dim,
      },
      rowsPerPageOptions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
      },
      rowsPerPageOption: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        borderRadius: 4,
        backgroundColor: admin.dataTable.filterBg,
      },
      rowsPerPageOptionSelected: {
        borderColor: admin.formInput.borderFocused,
        backgroundColor: admin.selector.itemSelectedBg,
      },
      rowsPerPageOptionText: {
        fontSize: 13,
        color: colors.text.dim,
      },
      rowsPerPageOptionTextSelected: {
        color: admin.dataTable.text,
        fontWeight: '600',
      },
      paginationButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 4,
        gap: 4,
      },
      paginationButtonDisabled: {
        opacity: 0.5,
      },
      paginationButtonText: {
        fontSize: 14,
        fontWeight: '500',
      },
      paginationButtonTextEnabled: {
        color: admin.dataTable.text,
      },
      paginationButtonTextDisabled: {
        color: colors.text.dim,
      },
      emptyStateContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
      },
      emptyStateTitle: {
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 8,
        color: admin.dataTable.text,
      },
      emptyStateMessage: {
        fontSize: 14,
        textAlign: 'center',
        color: colors.text.dim,
      },
      loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
      },
      loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: colors.text.dim,
      },
      loadingOverlay: {
        position: 'absolute',
        top: 8,
        right: 16,
        backgroundColor: admin.dataTable.headerBg,
        borderColor: admin.dataTable.border,
        borderWidth: 1,
        borderRadius: 12,
        padding: 8,
      },
      iconAccent: {
        color: admin.formInput.borderFocused,
      },
      iconMuted: {
        color: colors.text.dim,
      },
      errorAccent: {
        color: semantic.status.error,
      },
    });
  });
